/**
 * App Context
 *
 * Manages UI state and AI chat state.
 * Combines UI (category, view, search, modals) with AI (messages, loading).
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { nanoid } from 'nanoid';
import { openRouter } from '@/services/openRouter';
import { buildAIMessages } from '@/services/aiContext';
import { useData } from './DataContext';
import type { Category, ViewMode, ModalState, AIMessage, Item, ItemType } from '@/types';

// ============================================
// TYPES
// ============================================

interface OpenModalOptions {
  mode: 'add' | 'edit' | 'view' | 'delete' | 'settings';
  item?: Item | null | undefined;
  itemType?: ItemType | undefined;
  prefillTags?: string[] | undefined;
  prefillUrl?: string | undefined;
  prefillTitle?: string | undefined;
  prefillContent?: string | undefined;
  autoStartVoice?: boolean | undefined;
}

interface AppContextValue {
  // UI State
  category: Category;
  view: ViewMode;
  searchQuery: string;
  activeTag: string | null;
  modal: ModalState;
  modalKey: number;

  // UI Actions
  setCategory: (category: Category) => void;
  setView: (view: ViewMode) => void;
  setSearchQuery: (query: string) => void;
  setActiveTag: (tag: string | null) => void;
  openModal: (options: OpenModalOptions) => void;
  closeModal: () => void;

  // AI State
  aiMessages: AIMessage[];
  aiLoading: boolean;
  isAIPanelOpen: boolean;

  // AI Actions
  sendAIMessage: (content: string) => Promise<void>;
  clearAIChat: () => void;
  toggleAIPanel: () => void;
  openAIPanel: () => void;
  closeAIPanel: () => void;
}

// ============================================
// INITIAL STATE
// ============================================

const initialModal: ModalState = {
  isOpen: false,
  mode: 'add',
  item: null,
};

// ============================================
// CONTEXT
// ============================================

const AppContext = createContext<AppContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  // UI State
  const [category, setCategory] = useState<Category>('inbox');
  const [view, setView] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(initialModal);
  const [modalKey, setModalKey] = useState(0);

  // AI State
  const [aiMessages, setAIMessages] = useState<AIMessage[]>([]);
  const [aiLoading, setAILoading] = useState(false);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);

  // Get all items for AI context
  const { allItems } = useData();

  // ============================================
  // UI ACTIONS
  // ============================================

  const handleSetCategory = useCallback((newCategory: Category) => {
    setCategory(newCategory);
    // Clear tag filter when changing category
    setActiveTag(null);
  }, []);

  const openModal = useCallback((options: OpenModalOptions) => {
    setModalKey((prev) => prev + 1); // Increment to force remount on add mode
    setModal({
      isOpen: true,
      mode: options.mode,
      item: options.item ?? null,
      itemType: options.itemType,
      prefillTags: options.prefillTags,
      prefillUrl: options.prefillUrl,
      prefillTitle: options.prefillTitle,
      prefillContent: options.prefillContent,
      autoStartVoice: options.autoStartVoice,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModal(initialModal);
  }, []);

  // ============================================
  // AI ACTIONS
  // ============================================

  const sendAIMessage = useCallback(
    async (content: string) => {
      if (!openRouter.isConfigured()) {
        // Open settings modal to configure API key
        setModal({
          isOpen: true,
          mode: 'settings',
          item: null,
        });
        return;
      }

      // Create user message
      const userMessage: AIMessage = {
        id: nanoid(8),
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      };

      // Add user message to chat
      setAIMessages((prev) => [...prev, userMessage]);
      setAILoading(true);

      try {
        // Build messages for API (with context from items)
        const chatHistory = aiMessages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const apiMessages = buildAIMessages(allItems, chatHistory, content);

        // Call OpenRouter API
        const response = await openRouter.chat(apiMessages);

        // Create assistant message
        const assistantMessage: AIMessage = {
          id: nanoid(8),
          role: 'assistant',
          content: response,
          timestamp: new Date().toISOString(),
        };

        setAIMessages((prev) => [...prev, assistantMessage]);
      } catch (error) {
        // Create error message
        const errorMessage: AIMessage = {
          id: nanoid(8),
          role: 'assistant',
          content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          timestamp: new Date().toISOString(),
        };

        setAIMessages((prev) => [...prev, errorMessage]);
      } finally {
        setAILoading(false);
      }
    },
    [aiMessages, allItems]
  );

  const clearAIChat = useCallback(() => {
    setAIMessages([]);
  }, []);

  const toggleAIPanel = useCallback(() => {
    setIsAIPanelOpen((prev) => !prev);
  }, []);

  const openAIPanel = useCallback(() => {
    setIsAIPanelOpen(true);
  }, []);

  const closeAIPanel = useCallback(() => {
    setIsAIPanelOpen(false);
  }, []);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value: AppContextValue = {
    // UI State
    category,
    view,
    searchQuery,
    activeTag,
    modal,
    modalKey,

    // UI Actions
    setCategory: handleSetCategory,
    setView,
    setSearchQuery,
    setActiveTag,
    openModal,
    closeModal,

    // AI State
    aiMessages,
    aiLoading,
    isAIPanelOpen,

    // AI Actions
    sendAIMessage,
    clearAIChat,
    toggleAIPanel,
    openAIPanel,
    closeAIPanel,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// ============================================
// HOOK
// ============================================

export function useApp(): AppContextValue {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }

  return context;
}

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Get current modal state
 */
export function useModal(): ModalState & {
  open: (options: OpenModalOptions) => void;
  close: () => void;
} {
  const { modal, openModal, closeModal } = useApp();
  return {
    ...modal,
    open: openModal,
    close: closeModal,
  };
}

/**
 * Get current view mode
 */
export function useViewMode(): [ViewMode, (view: ViewMode) => void] {
  const { view, setView } = useApp();
  return [view, setView];
}

/**
 * Get current category
 */
export function useCategory(): [Category, (category: Category) => void] {
  const { category, setCategory } = useApp();
  return [category, setCategory];
}

/**
 * Get search state
 */
export function useSearch(): [string, (query: string) => void] {
  const { searchQuery, setSearchQuery } = useApp();
  return [searchQuery, setSearchQuery];
}

/**
 * Get AI panel state
 */
export function useAIPanel() {
  const {
    aiMessages,
    aiLoading,
    isAIPanelOpen,
    sendAIMessage,
    clearAIChat,
    toggleAIPanel,
    openAIPanel,
    closeAIPanel,
  } = useApp();

  return {
    messages: aiMessages,
    isLoading: aiLoading,
    isOpen: isAIPanelOpen,
    send: sendAIMessage,
    clear: clearAIChat,
    toggle: toggleAIPanel,
    open: openAIPanel,
    close: closeAIPanel,
  };
}
